import React, { useState, useMemo } from 'react';
import { Card, Button, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getGoogleMapsLink, safeFormatDate, getGoogleMapsDirectionsUrl } from '../../../shared/utils';
import { getLinkIconDisplay } from '../../../shared/linkIcons';

export default function TransportationActivityShare({ activity, dayId, nextActivityInfo }) {
  const { t } = useTranslation();
  // Local state for content visibility
  const [showContent, setShowContent] = useState(true);

  const nextStopLink = useMemo(() => {
    if (!nextActivityInfo || !nextActivityInfo.activity) return null;
    const directionsUrl = getGoogleMapsDirectionsUrl(activity, nextActivityInfo.activity, nextActivityInfo.role);
    if (!directionsUrl) return null;
    return { name: t('planDetail.days.nextStop'), url: directionsUrl };
  }, [activity, nextActivityInfo, t]);
  
  // Toggle content visibility
  const toggleContent = (e) => {
    e.stopPropagation();
    setShowContent(!showContent);
  };
  
  return (
    <Card className="mb-2 shadow-sm border-info">
      <Card.Body>
        {/* Header: activity name and collapse icon on same row (icon on end, respects RTL) */}
        <div className="mb-2 d-flex align-items-start gap-2">
          <h5 className="mb-0 flex-grow-1 text-break" style={{ minWidth: 0 }}>
            <span className="route-icon me-2"></span>
            {activity.name}
          </h5>
          <Button
            variant="outline-secondary"
            size="sm"
            className="flex-shrink-0"
            onClick={toggleContent}
          >
            <i className={`bi ${showContent ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
          </Button>
        </div>
        
        {/* Activity content */}
        <Collapse in={showContent}>
          <div>
            {/* Time information */}
            {(activity.startDateTime || activity.endDateTime) && (
              <div className="mb-2">
                <small className="text-muted">
                  <i className="bi bi-clock me-1"></i>
                  {activity.startDateTime && (
                    <span>
                      {t('planDetailShare.departure')}: {safeFormatDate(activity.startDateTime, 'HH:mm')}
                    </span>
                  )}
                  {activity.startDateTime && activity.endDateTime && (
                    <span> | </span>
                  )}
                  {activity.endDateTime && (
                    <span>
                      {t('planDetailShare.arrival')}: {safeFormatDate(activity.endDateTime, 'HH:mm')}
                    </span>
                  )}
                  {activity.estimatedDuration && (
                    <span className="ms-2">
                      ({t('planDetailShare.duration')}: {activity.estimatedDuration})
                    </span>
                  )}
                </small>
              </div>
            )}
            
            {/* From/To Locations */}
            {(activity.fromLocation || activity.toLocation) && (
              <div className="mb-2">
                <small className="text-muted">
                  <i className="bi bi-geo-alt me-1"></i>
                  {activity.fromLocation && activity.fromLocation.address && (
                    <span>
                      {t('planDetail.days.from')}: {activity.fromLocation.address}
                      <a 
                        href={getGoogleMapsLink(activity.fromLocation)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ms-1 text-decoration-none"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </a>
                    </span>
                  )}
                  {activity.fromLocation && activity.toLocation && activity.fromLocation.address && activity.toLocation.address && (
                    <span> → </span>
                  )}
                  {activity.toLocation && activity.toLocation.address && (
                    <span>
                      {t('planDetail.days.to')}: {activity.toLocation.address}
                      <a 
                        href={getGoogleMapsLink(activity.toLocation)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ms-1 text-decoration-none"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </a>
                    </span>
                  )}
                </small>
              </div>
            )}
            
            {/* Single location (fallback) */}
            {!activity.fromLocation && !activity.toLocation && activity.location && activity.location.address && (
              <div className="mb-2">
                <small className="text-muted">
                  <i className="bi bi-geo-alt-fill me-1"></i>
                  <span>{activity.location.address}</span>
                  <a 
                    href={getGoogleMapsLink(activity.location)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="ms-2 text-decoration-none"
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                  </a>
                </small>
              </div>
            )}
            
            {/* Description */}
            {activity.description && (
              <p className="mb-2">{activity.description}</p>
            )}
            
            {/* Notes - same order as PlanDetail: notes, links, images */}
            {activity.notes && (
              <div className="mt-2">
                <small className="text-muted d-block mb-1">
                  <i className="bi bi-journal-text me-1"></i>
                  {t('planDetailShare.notes')}
                </small>
                <small className="text-muted">{activity.notes}</small>
              </div>
            )}
            
            {/* Links and Next Stop */}
            {((activity.links && activity.links.length > 0) || nextStopLink) && (
              <div className="d-flex flex-wrap gap-1 mt-2 align-items-center">
                {activity.links && activity.links.map((link, index) => {
                  const iconDisplay = getLinkIconDisplay(link);
                  return (
                    <a 
                      key={index}
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="badge bg-light text-dark text-decoration-none border d-inline-flex align-items-center gap-1"
                      style={{ padding: '0.2rem 0.45rem' }}
                    >
                      <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '1.5em', height: '1.5em' }}>
                        {iconDisplay.iconUrl ? (
                          <img src={iconDisplay.iconUrl} alt="" style={{ width: '1.5em', height: '1.5em', objectFit: 'contain' }} />
                        ) : (
                          <i className={`bi ${iconDisplay.iconClass || 'bi-link-45deg'}`} style={{ fontSize: '1.5em', lineHeight: 1 }}></i>
                        )}
                      </span>
                      <span>{link.name || t('planDetailShare.link')}</span>
                    </a>
                  );
                })}
                {nextStopLink && (
                  <a 
                    href={nextStopLink.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="badge bg-light text-dark text-decoration-none border d-inline-flex align-items-center gap-1"
                    style={{ padding: '0.2rem 0.45rem' }}
                  >
                    <i className="bi bi-signpost-2" style={{ fontSize: '1.5em', lineHeight: 1 }}></i>
                    <span>{nextStopLink.name}</span>
                  </a>
                )}
              </div>
            )}
            
            {/* Images - at bottom, same layout as PlanDetail */}
            {activity.images && activity.images.length > 0 && (
              <div className="mt-2">
                {/* Mobile: Show only 2 images in a row, same size as PlanDetail */}
                <div className="d-md-none">
                  <div className="d-flex gap-2">
                    {activity.images.slice(0, 2).map((image, i) => (
                      image.url && (
                        <div 
                          key={i}
                          className="position-relative"
                          style={{ 
                            flex: activity.images.length === 1 ? '0 0 auto' : '1 1 calc(50% - 0.25rem)',
                            maxWidth: activity.images.length === 1 ? '120px' : 'none'
                          }}
                        >
                          <img 
                            src={image.url} 
                            alt={image.name || `${activity.name} - view ${i + 1}`}
                            className="img-thumbnail"
                            style={{ 
                              height: '120px',
                              width: activity.images.length === 1 ? '120px' : '100%',
                              objectFit: 'contain',
                              backgroundColor: '#f8f9fa'
                            }}
                          />
                        </div>
                      )
                    ))}
                  </div>
                </div>
                {/* Desktop: Grid layout - match PlanDetail */}
                <div className="d-none d-md-flex flex-wrap gap-2">
                  {activity.images.map((image, i) => (
                    image.url && (
                      <div 
                        key={i}
                        className="position-relative"
                        style={{ maxWidth: '200px' }}
                      >
                        <img 
                          src={image.url} 
                          alt={image.name || `${activity.name} - view ${i + 1}`}
                          className="img-thumbnail"
                          style={{ 
                            maxHeight: '150px', 
                            maxWidth: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
}
